import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useApi } from '@/hooks/useApi';
import { useI18n } from '@/contexts/I18nContext';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { UserDetail } from '@/types/user.types';

interface EditUserModalProps {
  open: boolean;
  onClose: () => void;
  user: UserDetail;
  onSuccess: () => void;
}

export function EditUserModal({ open, onClose, user, onSuccess }: EditUserModalProps) {
  const { tp } = useI18n();
  const { patch, loading } = useApi();

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    is_active: true,
  });

  useEffect(() => {
    if (open) {
      setForm({
        name: user.name ?? '',
        email: user.email ?? '',
        phone: user.phone ?? '',
        is_active: user.is_active,
      });
    }
  }, [open, user]);

  const canSave = form.name.trim().length >= 2 && form.email.trim().length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSave) return;

    try {
      // Field-level validation (email format, uniqueness) is enforced by the
      // backend — errors surface via useApi's toast.
      await patch(`/api/v1/dashboard/users/${user.id}`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        is_active: form.is_active,
      });
      toast.success(tp.users.updateSuccess);
      onSuccess();
      onClose();
    } catch {
      // useApi already displays the error toast
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{tp.users.editUserTitle}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-name">{tp.users.tableHeaderName}</Label>
            <Input
              id="edit-name"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-email">{tp.users.tableHeaderEmail}</Label>
            <Input
              id="edit-email"
              type="email"
              dir="ltr"
              value={form.email}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-phone">{tp.users.tableHeaderPhone}</Label>
            <PhoneInput
              value={form.phone}
              onChange={(val) => setForm((p) => ({ ...p, phone: val }))}
            />
          </div>

          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="space-y-0.5">
              <Label>{tp.users.fieldActiveLabel}</Label>
              <p className="text-xs text-muted-foreground">{tp.users.fieldActiveHint}</p>
            </div>
            <Switch
              checked={form.is_active}
              onCheckedChange={(checked) => setForm((p) => ({ ...p, is_active: checked }))}
            />
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {tp.users.cancelButton}
            </Button>
            <Button type="submit" disabled={loading || !canSave}>
              {loading && <Loader2 className="h-4 w-4 ms-2 animate-spin" />}
              {loading ? tp.users.savingButton : tp.users.saveButton}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
